import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StoreNavbar from '@/components/feature/StoreNavbar';
import SectionLink from '@/components/feature/SectionLink';
import { verifyEpaycoTransaction, type EpaycoResponseStatus } from '@/services/gateways';
import { sendOnlinePaymentApprovalEmail } from '@/services/notifications';
import { PCP_OFFICIAL_WHATSAPP } from '@/services/finances';
import { clearPendingEpayco, loadPendingEpayco } from '@/lib/pendingPayment';
import { useMetaPixel } from '@/context/MetaPixelContext';
import { useCart } from '@/context/CartContext';

type Status = EpaycoResponseStatus | 'loading';

export default function PaymentConfirmPage() {
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState<Status>('loading');
  const [orderNumber, setOrderNumber] = useState('');
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const { track, trackPurchase } = useMetaPixel();
  const { removeItems, clearCart } = useCart();
  const purchaseFiredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setErrorMsg('');

    // Reúne TODOS los parámetros devueltos por ePayco (ref_payco, x_...) y los
    // valida del lado del servidor (firma + estado autoritativo).
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    verifyEpaycoTransaction(params)
      .then((res) => {
        if (cancelled) return;
        setOrderNumber(res.orderNumber);
        setTotal(res.total);
        setStatus(res.status ?? 'PENDING');
        // Si el pago quedó aprobado, ya no hay pago pendiente que retomar y
        // recién ahora vaciamos del carrito los productos de este pedido (sin
        // borrar artículos que el cliente haya agregado después).
        if (res.status === 'APPROVED') {
          const pending = loadPendingEpayco();
          if (pending?.orderedKeys?.length) {
            removeItems(pending.orderedKeys);
          } else {
            clearCart();
          }
          clearPendingEpayco();
        }
        // Purchase: el pedido de ePayco queda pagado solo cuando la transacción
        // se aprueba. Se dispara una vez (pixel + CAPI, mismo event_id).
        if (res.status === 'APPROVED' && res.updated && res.orderNumber && !purchaseFiredRef.current) {
          purchaseFiredRef.current = true;
          trackPurchase({
            eventId: res.orderNumber,
            value: Number(res.total ?? 0),
            currency: 'COP',
            orderNumber: res.orderNumber,
          });
        }
        // Disparo del correo de confirmación SOLO cuando el pago queda aprobado
        // por primera vez (`updated` marca la transición).
        if (res.status === 'APPROVED' && res.updated && res.orderNumber) {
          void sendOnlinePaymentApprovalEmail(res.orderNumber);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : 'No se pudo verificar el pago.');
        setStatus('PENDING');
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Si el pago se abrió en una ventana externa (modo 'popup'), avisamos a la
  // ventana que nos abrió para que refleje el resultado del pago.
  useEffect(() => {
    if (!window.opener || status === 'loading') return;
    window.opener.postMessage(
      { type: 'epayco-pay-done', status, orderNumber },
      window.location.origin,
    );
  }, [status, orderNumber]);

  const reference = orderNumber || searchParams.get('x_id_invoice') || searchParams.get('ref_payco') || '';

  return (
    <div className="relative z-0 min-h-screen bg-background-50">
      <div className="paw-watermark absolute inset-0 -z-10 pointer-events-none opacity-[0.04]" aria-hidden="true" />
      <StoreNavbar />

      <div className="max-w-lg mx-auto px-4 py-12 md:py-16">
        <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8 text-center">
          {status === 'loading' ? (
            <>
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-loader-4-line animate-spin text-accent-600 text-3xl"></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                Confirmando tu pago...
              </h1>
              <p className="text-foreground-600 text-sm">
                Estamos verificando el estado de tu transacción en ePayco.
              </p>
            </>
          ) : status === 'APPROVED' ? (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-check-line text-emerald-600 text-3xl"></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                ¡Pago aprobado!
              </h1>
              <p className="text-foreground-600 text-sm mb-6">
                Gracias por tu compra, Patyfamilia. Tu pedido{' '}
                <strong className="text-accent-600">{orderNumber}</strong> quedó pagado y en
                preparación.
              </p>

              {total > 0 && (
                <div className="bg-background-50 rounded-xl p-4 mb-6 text-left space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground-500">Total pagado</span>
                    <span className="font-bold text-foreground-950">${total.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div className="p-4 rounded-xl bg-primary-500 border border-primary-600 mb-6 flex items-start gap-3 text-left">
                <i className="ri-gift-2-line text-foreground-950 text-xl mt-0.5"></i>
                <p className="text-sm text-foreground-950 leading-relaxed font-semibold">
                  No olvides que por tu compra recibirás la <strong>Bandana de la Manada</strong> y
                  la <strong>Bolsa Ecológica PCP</strong>. ¡Gracias por apoyar la causa!
                </p>
              </div>

              {orderNumber && (
                <a
                  href={`https://wa.me/${PCP_OFFICIAL_WHATSAPP.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola PCP, el pago del pedido *${orderNumber}* fue aprobado. ¡Gracias!`)}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => track('Lead', { content_name: 'WhatsApp', currency: 'COP', value: 0 })}
                  className="inline-flex items-center justify-center gap-2 w-full py-3 bg-white border border-emerald-300 text-emerald-600 rounded-xl font-semibold text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap mb-3"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Confirmar por WhatsApp
                </a>
              )}

              {orderNumber && (
                <Link
                  to={`/pedidos?pedido=${encodeURIComponent(orderNumber)}`}
                  className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap mb-3"
                >
                  <i className="ri-truck-line text-lg"></i>
                  Ver estado de mi pedido
                </Link>
              )}

              <SectionLink
                section="catalogo"
                className="inline-flex items-center justify-center gap-2 w-full py-3 bg-white border border-background-300 text-foreground-700 rounded-xl font-semibold text-sm hover:bg-background-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-store-2-line text-lg"></i>
                Volver al catálogo
              </SectionLink>
            </>
          ) : status === 'DECLINED' || status === 'ERROR' ? (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-close-line text-red-600 text-3xl"></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                Pago no completado
              </h1>
              <p className="text-foreground-600 text-sm mb-6">
                Tu transacción fue {status === 'DECLINED' ? 'rechazada' : 'cancelada'}. Puedes
                intentar nuevamente o finalizar tu pedido por otro medio.
              </p>
              {reference && (
                <div className="bg-background-50 rounded-xl px-4 py-3 mb-6 text-sm text-foreground-600">
                  Pedido:{' '}
                  <span className="font-semibold text-foreground-900">{reference}</span>
                </div>
              )}
              <Link
                to="/checkout"
                className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap mb-3"
              >
                <i className="ri-refresh-line text-lg"></i>
                Reintentar pago
              </Link>
              <SectionLink
                section="catalogo"
                className="inline-flex items-center justify-center gap-2 w-full py-3 bg-white border border-background-300 text-foreground-700 rounded-xl font-semibold text-sm hover:bg-background-50 transition-colors whitespace-nowrap"
              >
                Volver al catálogo
              </SectionLink>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-time-line text-accent-600 text-3xl"></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                Pago en verificación
              </h1>
              <p className="text-foreground-600 text-sm mb-6">
                Tu pago está en proceso. Te notificaremos por WhatsApp apenas se confirme la
                transacción.
              </p>

              {reference && (
                <div className="bg-background-50 rounded-xl px-4 py-3 mb-6 text-sm text-foreground-600">
                  Referencia:{' '}
                  <span className="font-semibold text-foreground-900">{reference}</span>
                </div>
              )}

              {errorMsg && (
                <p className="text-xs text-red-600 mb-4 text-left">{errorMsg}</p>
              )}

              <SectionLink
                section="catalogo"
                className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-accent-500 text-white rounded-xl font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-store-2-line text-lg"></i>
                Volver al catálogo
              </SectionLink>
            </>
          )}
        </div>
      </div>
    </div>
  );
}